export type DashboardWidgetType =
  | 'placeholder'
  | 'iframe'
  | 'text'
  | 'viz_tabs'
  | 'chart_bar'
  | 'chart_line'
  | 'chart_area'
  | 'chart_donut'
  | 'chart_radar'
  | 'chart_scatter'
  | 'chart_treemap'
  | 'calendar_heatmap'
  | 'gauge'
  | 'indicator'
  | 'data_table'
  | 'device_list'
  | 'activity_timeline'
  | 'live_status'
  | 'web_map'
  | 'spatial_analytics'
  | 'geofence'
  | 'widget_stack'
  | 'widget_group';

/** Row for Date header widget -> Actions tab (filter targets). */
export interface DashboardDateFilterTargetRow {
  layoutId: string;
  label: string;
  perWidgetTimeSupported: boolean;
  widgetType: DashboardWidgetType;
}

/** One panel inside a tabbed viz widget */
export type DashboardVizKind = 'bar' | 'line' | 'area' | 'donut' | 'radar' | 'scatter' | 'treemap' | 'calendar' | 'text';

export interface DashboardTabDefinition {
  id: string;
  label: string;
  viz: DashboardVizKind;
  /** When viz is `text` */
  textBody?: string;
}

export interface VizTabsWidgetConfig {
  tabs?: DashboardTabDefinition[];
}

/** Field name from the bound Web Map layer used to group chart data. */
export type ChartGroupByField = string;

export type ChartLegendMode = 'hidden' | 'value' | 'percent';

/** Bottom = horizontal strip; side = vertical legend. */
export type ChartLegendPlacement = 'bottom' | 'side';

export type ChartAxisLabelMode = 'auto' | 'horizontal' | 'rotate-45' | 'rotate-90' | 'stacked';

export type ChartColorPaletteId =
  | 'default'
  | 'earth'
  | 'vivid'
  | 'neon'
  | 'pastel'
  | 'ocean'
  | 'sunset'
  | 'midnight'
  | 'cyber'
  | 'forest'
  | 'autumn'
  | 'berry'
  | 'retro'
  | 'tropical'
  | 'slate'
  | 'candy'
  | 'jewel';

export interface StandaloneChartConfig {
  chartTitle?: string;
  groupBy?: ChartGroupByField;
  /** Calendar: trailing weeks to show (default 6) */
  heatmapWeeks?: number;
  /** Calendar: square cell edge length in px (4-96). Omit for responsive sizing. */
  heatmapCellSizePx?: number;
  /** Donut hole size 0-85 (% of outer radius). Default 55. */
  donutInnerRadiusPercent?: number;
  /** Pie start angle in degrees (Recharts). Default 90. */
  donutStartAngle?: number;
  legendMode?: ChartLegendMode;
  legendPlacement?: ChartLegendPlacement;
  /** Legend / slice label font size (px). Default 11. */
  chartLabelFontSize?: number;
  chartTextColor?: string;
  /** Layout for category labels on Cartesian chart x-axes. */
  chartAxisLabelMode?: ChartAxisLabelMode;
  chartColorPaletteId?: ChartColorPaletteId;
}

export interface IframeWidgetConfig {
  iframeUrl?: string;
  iframeAllow?: string;
}

export interface GaugeWidgetConfig {
  gaugeMin?: number;
  gaugeMax?: number;
  gaugeLabel?: string;
  gaugeColor?: string;
}

export interface DataTableWidgetConfig {
  tablePageSize?: number;
  tableCompact?: boolean;
  /** Which columns to show (empty/undefined = all). Order is display order. */
  tableVisibleColumns?: string[];
  /** Rename map: source field name → display label. */
  tableColumnLabels?: Record<string, string>;
}

export interface ScatterWidgetConfig {
  scatterXField?: string;
  scatterYField?: string;
}

export interface TextWidgetConfig {
  title?: string;
  body?: string;
}

/** Visual styling for a dashboard tile (chrome + typography hints). */
export interface DashboardWidgetStyle {
  cardBackground?: string;
  cardBorderColor?: string;
  /** px */
  cardBorderRadius?: number;
  /** 0-1 applied to the whole tile chrome */
  cardOpacity?: number;
  titleColor?: string;
  titleFontSize?: 'small' | 'medium' | 'large';
  contentTextColor?: string;
  /** Scales maps, charts, and text inside the tile (not the header). 1 = default. */
  contentScale?: number;
  /** Per-widget override: hide the card title/header bar. */
  hideHeader?: boolean;
  /** Per-widget override: hide data-source labels inside the widget body. */
  hideLabel?: boolean;
}

/** Dashboard-wide visual defaults applied to all widgets (per-widget style overrides these). */
export interface DashboardTheme {
  dashboardBackground?: string;
  widgetBackground?: string;
  widgetBorderColor?: string;
  widgetBorderRadius?: number;
  widgetOpacity?: number;
  titleColor?: string;
  contentTextColor?: string;
  hideHeaders?: boolean;
  hideLabels?: boolean;
}

export type IndicatorFilterOperator =
  | 'eq'
  | 'neq'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'is_empty'
  | 'is_not_empty';

export interface IndicatorWidgetConfig {
  /** Layer field name to filter on. */
  indicatorFilterField?: string;
  indicatorFilterOperator?: IndicatorFilterOperator;
  /** Empty = no filter (count all features in the bound layer). */
  indicatorFilterValue?: string;
  /** Shown above the main line; `{value}` allowed. */
  indicatorTextTop?: string;
  /** Main line; `{value}` = computed count. Default set in UI. */
  indicatorTextMiddle?: string;
  indicatorTextBottom?: string;
  indicatorColorTop?: string;
  indicatorColorMiddle?: string;
  indicatorBgMiddle?: string;
  indicatorColorBottom?: string;
  /** Inner face background (behind text stack). */
  indicatorFaceBg?: string;
  /** Accent for border glow / highlights. */
  indicatorAccent?: string;
  indicatorShowLastUpdated?: boolean;
}

export type WidgetStackNavStyle = 'tabs' | 'carousel';

export interface WidgetStackConfig {
  childIds?: string[];
  activeChildIndex?: number;
  navStyle?: WidgetStackNavStyle;
  /** Per-child custom tab labels keyed by child widget id. */
  tabLabels?: Record<string, string>;
}

export type WidgetGroupDirection = 'row' | 'column';

export interface WidgetGroupConfig {
  groupChildIds?: string[];
  direction?: WidgetGroupDirection;
  /** Flex ratios for each child (same length as groupChildIds). Omit for equal split. */
  splitRatios?: number[];
}

import type {
  LayerDisplayMode,
  WebMapLayerConfig,
  WebMapWidgetOptions,
  WebMapWidgetConfig,
  WidgetDataBinding,
  SpatialAnalyticsWidgetConfig,
} from '../types/webMap';

export type {
  LayerDisplayMode,
  WebMapLayerConfig,
  WebMapWidgetOptions,
  WebMapWidgetConfig,
  WidgetDataBinding,
  SpatialAnalyticsWidgetConfig,
};

/** A locally-stored geofence polygon (not connected to COP backend). */
export interface LocalGeofence {
  id: string;
  name: string;
  geometry: { type: 'Polygon'; coordinates: number[][][] };
  color?: string;
  createdAt: string;
}

export interface GeofenceWidgetConfig {
  geofences?: LocalGeofence[];
  basemap?: 'streets' | 'satellite' | 'dark' | 'light' | 'outdoors';
  initialExtent?: { center: [number, number]; zoom: number };
  /** Default color for new geofence polygons. */
  geofenceDefaultColor?: string;
}

export type DeviceListSortField = 'name' | 'timestamp' | 'type';
export type DeviceListDensity = 'compact' | 'comfortable';
export type DeviceListSelectionMode = 'single' | 'multiple';
export type DeviceListIconMode = 'none' | 'symbol';

export interface DeviceListAction {
  type: 'filter' | 'flash' | 'pan' | 'zoom' | 'popup' | 'follow' | 'streetview';
  enabled: boolean;
}

export type DeviceFieldDisplayType =
  | 'text'
  | 'badge'
  | 'elapsed_time'
  | 'number'
  | 'progress'
  | 'link'
  | 'boolean'
  | 'datetime'
  | 'threshold'
  | 'tag_list'
  | 'image'
  | 'coordinate'
  | 'hidden';

export interface DeviceFieldColorEntry {
  value: string;
  bg: string;
  fg: string;
}

export interface ThresholdBand {
  max: number;
  color: string;
  label?: string;
}

export interface DeviceFieldFormatRule {
  id: string;
  field: string;
  label?: string;
  displayType: DeviceFieldDisplayType;
  prefix?: string;
  suffix?: string;
  colorMap?: DeviceFieldColorEntry[];
  linkTemplate?: string;
  progressMax?: number;
  order: number;
  span?: boolean;
  /** Boolean display */
  trueLabel?: string;
  falseLabel?: string;
  trueColor?: string;
  falseColor?: string;
  /** Threshold bands (evaluated low→high; value <= band.max gets band.color) */
  thresholdBands?: ThresholdBand[];
  /** Tag list separator regex (default comma) */
  tagSeparator?: string;
  tagBg?: string;
  tagFg?: string;
  /** Image display */
  imageHeight?: number;
  /** Datetime format: 'date' | 'time' | 'datetime' | 'relative' */
  dateFormat?: 'date' | 'time' | 'datetime' | 'relative';
  /** Second field for coordinates (longitude or latitude) */
  coordField2?: string;
  /** Text styling */
  align?: 'left' | 'center' | 'right';
  bold?: boolean;
  italic?: boolean;
  fontSize?: 'small' | 'normal' | 'large';
  textColor?: string;
}

export interface DeviceListWidgetConfig {
  /** Field to use as the card header / entity name. */
  nameField?: string;
  /** Field to use as the category / type badge. */
  typeField?: string;
  /** Field to use for sorting by time. */
  timestampField?: string;
  /** Additional fields to display on each card. */
  detailFields?: string[];
  /** Visual format rules for field-level display (replaces plain detailFields when present). */
  formatRules?: DeviceFieldFormatRule[];
  sortField?: DeviceListSortField;
  sortDir?: 'asc' | 'desc';
  density?: DeviceListDensity;
  showSearch?: boolean;
  maxItems?: number;
  /**
   * Rich-text template for the card body. Use {fieldName} placeholders.
   * Example: "Dwell Start: {idleStart}\nDevice: {deviceid}"
   */
  lineTemplate?: string;
  iconMode?: DeviceListIconMode;
  /** When set, only show the most recent entry per unique value in this field. */
  uniqueField?: string;
  /** Accent color for names and highlights. */
  highlightColor?: string;
  selectionMode?: DeviceListSelectionMode;
  /** Actions triggered when an item is selected. */
  actions?: DeviceListAction[];
}

export interface ActivityTimelineWidgetConfig {
  /** Field that contains the timestamp for timeline ordering. */
  timestampField?: string;
  /** Field to use as the event title / label. */
  labelField?: string;
  /** Field for the event description / detail line. */
  descriptionField?: string;
  /** Field to determine the color/type of the timeline dot. */
  categoryField?: string;
  maxItems?: number;
  /** 'vertical' (default) or 'horizontal' layout. */
  orientation?: 'vertical' | 'horizontal';
  /** 'timeline' (dot+spine) or 'chart' (histogram bars). */
  viewMode?: 'timeline' | 'chart';
  /** Color palette id from chartColorPalettes. */
  colorPalette?: ChartColorPaletteId;
}

export interface LiveStatusWidgetConfig {
  /** Field to group entities by (e.g. entity_type, device_type). */
  groupByField?: string;
  /** Field that contains a timestamp to determine online/offline status. */
  timestampField?: string;
  /** Minutes since last update to be considered "active". */
  activeThresholdMinutes?: number;
  /** Additional numeric fields to aggregate in the summary. */
  statFields?: string[];
}

export type SidePanelAnchor = 'left' | 'right' | 'bottom';

/** Dashboard-level side panel that docks to the edge of the dashboard. */
export interface DashboardSidePanelConfig {
  enabled: boolean;
  /** Ordered list of child widget IDs rendered inside the panel. */
  childWidgetIds: string[];
  /** Which edge the panel docks to. */
  anchor: SidePanelAnchor;
  /** Width (left/right) or height (bottom) of the open panel in pixels. */
  panelWidth: number;
  /** Flex ratios for each child (same length as childWidgetIds). Omit for equal split. */
  splitRatios?: number[];
}

export type DashboardLayoutMode = 'fit' | 'scroll';

export interface DashboardWidgetInstance {
  type: DashboardWidgetType;
  config?: TextWidgetConfig &
    VizTabsWidgetConfig &
    StandaloneChartConfig &
    IndicatorWidgetConfig &
    IframeWidgetConfig &
    GaugeWidgetConfig &
    DataTableWidgetConfig &
    ScatterWidgetConfig &
    WebMapWidgetConfig &
    SpatialAnalyticsWidgetConfig &
    GeofenceWidgetConfig &
    DeviceListWidgetConfig &
    ActivityTimelineWidgetConfig &
    LiveStatusWidgetConfig &
    WidgetStackConfig &
    WidgetGroupConfig & {
      customTitle?: string;
      style?: DashboardWidgetStyle;
      dataBinding?: WidgetDataBinding;
      crossFilterTargets?: CrossFilterTarget[];
    };
}

/** Full-width bar above the grid; not part of react-grid-layout. */
export type DashboardHeaderTitleSize = 'small' | 'medium' | 'large';

export interface DashboardHeaderChrome {
  title: string;
  subtitle?: string;
  logoUrl?: string;
  logoAlign?: 'left' | 'right';
  backgroundColor?: string;
  titleColor?: string;
  subtitleColor?: string;
  titleFontSize?: DashboardHeaderTitleSize;
  showLastUpdated?: boolean;
}

/** Optional strip under the main header (e.g. CUI notice). */
export interface DashboardBannerStrip {
  text: string;
  backgroundColor?: string;
  textColor?: string;
}

export type HeaderCategoryPresentation = 'dropdown' | 'inline';

export type HeaderCategorySelectionMode = 'single' | 'multiple';

export type HeaderCategoryListHeight = 'compact' | 'extended';

export type HeaderCategoryOperator = 'include' | 'exclude';

export type DashboardHeaderWidget =
  | {
      id: string;
      type: 'header_category';
      label?: string;
      /** Web Map layer that supplies options and receives this filter. Omit for legacy all-layer behavior. */
      layerId?: string;
      /** Layer field name to filter by. */
      schemaField: string;
      placeholderText?: string;
      presentation?: HeaderCategoryPresentation;
      selectionMode?: HeaderCategorySelectionMode;
      showSearch?: boolean;
      showResetSelectAll?: boolean;
      listHeight?: HeaderCategoryListHeight;
      categoryOperator?: HeaderCategoryOperator;
    }
  | {
      id: string;
      type: 'header_date';
      label?: string;
      hintText?: string;
      timeFilterExcludedWidgetIds?: string[];
    }
  | {
      id: string;
      type: 'header_number';
      label?: string;
      mode: 'static' | 'dynamic';
      staticValue?: string;
      /** Layer field or metric key for dynamic value. */
      metric?: string;
    };

/* ------------------------------------------------------------------
 * Cross-filter configuration (Actions tab)
 * ------------------------------------------------------------------ */

export interface CrossFilterFieldMapping {
  sourceField: string;
  targetField: string;
}

export interface CrossFilterTarget {
  widgetId: string;
  enabled: boolean;
  renderOnlyWhenFiltered?: boolean;
  fieldMappings: CrossFilterFieldMapping[];
}

export const DASHBOARD_BUILDER_STORAGE_KEY = 'pathfinder_dashboard_builder_v1';

export const DASHBOARD_BUILDER_SCHEMA_VERSION = 1 as const;

export interface DashboardBuilderState {
  version: typeof DASHBOARD_BUILDER_SCHEMA_VERSION;
  /** Full-width header chrome + filter widgets (optional; merged from defaults on load). */
  header?: DashboardHeaderChrome;
  headerWidgets?: DashboardHeaderWidget[];
  banner?: DashboardBannerStrip;
  /** Dashboard-wide visual theme (colors, borders, display toggles). */
  theme?: DashboardTheme;
  /** react-grid-layout items -- `i` matches keys in `widgets` */
  layout: Array<{
    i: string;
    x: number;
    y: number;
    w: number;
    h: number;
    minW?: number;
    minH?: number;
  }>;
  widgets: Record<string, DashboardWidgetInstance>;
  /** Collapsible side panel that docks to the dashboard edge. */
  sidePanel?: DashboardSidePanelConfig;
  /** 'fit' = fill viewport (no scroll), 'scroll' = full-page scrollable grid. */
  layoutMode?: DashboardLayoutMode;
  /** ID of the template used to create this dashboard (informational). */
  templateId?: string;
}
